import type { SVGProps } from 'react'

/**
 * Иконки нарисованы здесь, а не подключены библиотекой.
 *
 * Их полтора десятка, каждая — четыре строки разметки. Библиотека иконок
 * добавила бы зависимость, обновления и несколько сотен килобайт ради
 * того, что помещается в один файл. Когда иконок станет пятьдесят, этот
 * расчёт изменится — тогда и поменяем, интерфейс знает только имена.
 *
 * Все иконки рисуются обводкой в `currentColor` и наследуют цвет текста:
 * поэтому активный пункт меню, ссылка и предупреждение окрашиваются
 * одним правилом на родителе, а не отдельным для значка.
 */
type IconProps = SVGProps<SVGSVGElement> & { size?: number }

function Svg({ size = 16, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  )
}

export function IconGauge(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M2.5 10a5.5 5.5 0 0 1 11 0" />
      <path d="M8 10 11 6.4" />
    </Svg>
  )
}

export function IconChat(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M2.5 4.5A1.5 1.5 0 0 1 4 3h8a1.5 1.5 0 0 1 1.5 1.5v5A1.5 1.5 0 0 1 12 11H6.5L3.5 13.5V11A1.5 1.5 0 0 1 2.5 9.5z" />
    </Svg>
  )
}

export function IconPeople(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="6" cy="5.5" r="2.3" />
      <path d="M1.8 13c.4-2.3 2.1-3.6 4.2-3.6S9.8 10.7 10.2 13" />
      <path d="M10.6 4.2a2.2 2.2 0 0 1 0 4.2M11.4 9.7c1.6.4 2.6 1.6 2.9 3.3" />
    </Svg>
  )
}

export function IconDocument(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 2.5h5.4l3.1 3.1v7.9H4z" />
      <path d="M9.3 2.6v3.1h3.1M6 8.6h4.5M6 11h3" />
    </Svg>
  )
}

/** Коробка — каталог товаров. */
export function IconBox(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 7.5 12 3l9 4.5v9L12 21l-9-4.5v-9Z" />
      <path d="M3 7.5 12 12l9-4.5M12 12v9" />
    </Svg>
  )
}

/**
 * Воронка: сужающиеся полосы. Не столбики диаграммы — те означали бы
 * сравнение величин, а здесь важно другое: каждая следующая ступень
 * уже предыдущей, и это видно до чтения подписей.
 */
export function IconFunnel(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 5h16M6.5 11h11M10 17h4M11 17v4h2v-4" />
    </Svg>
  )
}

/** Антенна: линия связи, через которую приходят сообщения. */
export function IconAntenna(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 12v9M8.5 21h7" />
      <path d="M8.8 8.8a4.5 4.5 0 0 1 6.4 0M5.9 5.9a8.6 8.6 0 0 1 12.2 0" />
      <circle cx="12" cy="12" r="1.4" />
    </Svg>
  )
}

export function IconAgent(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="3" y="5.5" width="10" height="7.5" rx="2" />
      <path d="M8 5.5V3M6 9h.01M10 9h.01" />
    </Svg>
  )
}

export function IconKey(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="5.5" cy="8" r="2.6" />
      <path d="M8.1 8H14M11.6 8v2.3M13.2 8v1.8" />
    </Svg>
  )
}

export function IconList(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3 4h10M3 8h10M3 12h6" />
    </Svg>
  )
}

export function IconAlert(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M8 2.8 14 13H2z" />
      <path d="M8 6.6v3M8 11.4h.01" />
    </Svg>
  )
}

export function IconChevronDown(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 6.5 8 10.5l4-4" />
    </Svg>
  )
}

export function IconSun(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="8" cy="8" r="2.8" />
      <path d="M8 1.6v1.5M8 12.9v1.5M1.6 8h1.5M12.9 8h1.5M3.5 3.5l1 1M11.5 11.5l1 1M12.5 3.5l-1 1M4.5 11.5l-1 1" />
    </Svg>
  )
}

export function IconMoon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M13.2 9.6A5.6 5.6 0 0 1 6.4 2.8a5.6 5.6 0 1 0 6.8 6.8z" />
    </Svg>
  )
}

export function IconLogout(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M6.2 3H4a1.5 1.5 0 0 0-1.5 1.5v7A1.5 1.5 0 0 0 4 13h2.2" />
      <path d="M10 5.2 12.8 8 10 10.8M12.8 8H6" />
    </Svg>
  )
}

export function IconMonitor(props: IconProps) {
  return (
    <Svg {...props}>
      <rect x="2" y="3" width="12" height="8" rx="1.4" />
      <path d="M6 13.5h4" />
    </Svg>
  )
}
